import { useContext, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  FormHelperText,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { styled } from "@mui/material/styles";
import ReCAPTCHA from "react-google-recaptcha";
import { useTheme } from "@mui/material/styles";
import axios from "axios";

import schema from "./schema";

import Form from "./style";
import TowFAComponent from "./TowFAComponent";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AppContext } from "../../scripts/context/App";
import { Link } from "react-router-dom";

export default function LoginForm() {
  const navigate = useNavigate();
  const recaptchaRef = useRef<any>(null);
  const theme = useTheme();
  const { setState } = useContext(AppContext);
  const [userInformation, setUserInformation] = useState({
    email: "",
    password: "",
    "g-recaptcha-response": "",
    one_time_password: "",
  });
  const [errors, setErrors] = useState({
    email: false,
    password: false,
    "g-recaptcha-response": false,
  });
  const [twoFA, setTwoFA] = useState(false);
  const [loading, setLoading] = useState(false);

  const handelSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrors({ email: false, password: false, "g-recaptcha-response": false });

    schema
      .validate(userInformation, { abortEarly: false })
      .then(() => {
        setLoading(true);
        axios
          .post("/login", userInformation)
          .then(async ({ data }) => {
            if (!data.token) {
              setLoading(false);
              return setTwoFA(true);
            }
            localStorage.setItem("token", data.token);
            axios.defaults.headers.common["Authorization"] =
              data.token.includes("Bearer")
                ? data.token
                : `Bearer ${data.token}`;
            try {
              const roles_and_permissions = await Promise.all([
                data.user.permissions.includes("view_system_role")
                  ? axios.get("/roles/get-roles", {
                    params: {
                      getAll: 1,
                    },
                  })
                  : Promise.resolve(null),
                axios.get("/roles/get-permissions"),
              ]);

              setState({
                user: data.user,
                roles_permissions: roles_and_permissions[0]?.data || [],
                permissions: roles_and_permissions[1].data.data || [],
              });
              setLoading(false);
            } catch (error) {
              console.log(error);
            }

            data.user.permissions.includes("view_claimers") ? navigate("/dashboard/claimer-management") : navigate("/");
          })
          .catch((error) => {
            setLoading(false);
            setTwoFA(false);
            if (error.response.data.errors) {
              toast.error("Invalid credentials");
              Object.keys(error.response.data.errors).includes(
                "g-recaptcha-response"
              ) && recaptchaRef.current.reset();

              return;
            }
            toast.error(error.response.data.message);
          });
      })
      .catch((error) => {
        setErrors(
          error.inner.reduce((acc: any, err: any) => {
            acc[err.path] = err.message;
            return acc;
          }, {})
        );
      });
  };

  const handelChanges = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setUserInformation({ ...userInformation, [name]: value });
  };

  const checkReCAPTCHA = (captcha: string | null) => {
    if (!captcha) return;

    setUserInformation({ ...userInformation, "g-recaptcha-response": captcha });
  };

  const CustomStylesForHelpText = styled(FormHelperText)({
    color: theme.palette.error.main,
    textAlign: "center",
  });

  return (
    <>
      {twoFA && <TowFAComponent
        setTwoFA={setTwoFA}
        twoFA={twoFA}
        userInformation={userInformation}
        setUserInformation={setUserInformation}
      />}
      <Form onSubmit={handelSubmit}>
        <TextField
          inputProps={{
            style: { color: "white" },
            autoComplete: "username",
            className: "dark-input-label",
          }}
          size="small"
          InputLabelProps={{ style: { color: "rgb(255 255 255 / 60%)" } }}
          error={Boolean(errors.email)}
          helperText={errors.email}
          value={userInformation.email}
          onChange={handelChanges}
          name="email"
          id="email"
          label="Email address"
          variant="standard"
        />
        <TextField
          inputProps={{
            style: { color: "white" },
            type: "password",
            autoComplete: "current-password",
            className: "dark-input-label",
          }}
          InputLabelProps={{ style: { color: "rgb(255 255 255 / 60%)" } }}
          error={Boolean(errors.password)}
          helperText={errors.password}
          value={userInformation.password}
          onChange={handelChanges}
          name="password"
          id="password"
          label="Password"
          variant="standard"
          size="small"
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "5px",
          }}
        >
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6LdSxl4eAAAAAIo3jSjiApiwV8G1l0z-FDUG_e7T"
            onChange={(captcha: string | null) => checkReCAPTCHA(captcha)}
            style={{ display: "flex", justifyContent: "center" }}
            onExpired={() =>
              setUserInformation({
                ...userInformation,
                "g-recaptcha-response": "",
              })
            }
            type="image"
          />
          {errors["g-recaptcha-response"] && (
            <CustomStylesForHelpText>
              {errors["g-recaptcha-response"]}
            </CustomStylesForHelpText>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={loading}
          startIcon={
            loading && (
              <CircularProgress
                sx={{ width: "15px !important", height: "15px !important" }}
              />
            )
          }
        >
          Sign In
        </Button>
        <Box
          sx={{
            textAlign: "center",
            a: {
              color: "#fff",
              textDecoration: "none",
              fontSize: "13px",
              ":hover": {
                textDecoration: "underline",
              },
            },
          }}
        >
          <Link to="/password/reset">Forget password</Link>
        </Box>
      </Form>
    </>
  );
}
